import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { Calendar } from 'lucide-react';
import { useId } from 'react';
import { DayPicker, type PropsBase, type PropsSingle } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import Field, { type FieldProps } from '../Field';
import InputGroup from '@/components/common/Input/InputGroup';
import useDateFormats from '@/utils/useDateFormats';
import 'react-day-picker/dist/style.css';

export type DatePickerProps = Omit<FieldProps, 'id' | 'children'> & {
    id?: string;
    value: Date | undefined;
    onChange?: PropsSingle['onSelect'];
    inputState?: 'valid' | 'invalid' | 'normal';
    disabled?: boolean;
    pickerProps?: Omit<PropsSingle, 'classNames' | 'selected' | 'onSelect'>;
    name?: string;
    /**
     * Font size for label. Inherited from FieldProps.
     * @default 'base'
     */
    labelSize?: 'sm' | 'base';
    /**
     * Font weight for label. Inherited from FieldProps.
     * @default 'semibold'
     */
    labelWeight?: 'medium' | 'semibold';
    /**
     * Whether to capitalize the label. Inherited from FieldProps.
     * @default true
     */
    labelCapitalize?: boolean;
};

const classNames: PropsBase['classNames'] = {
    selected: 'text-teal-400',
    today: 'text-blue-400',
    chevron: 'fill-teal-500',
};

const DatePicker = ({
    value,
    onChange,
    id,
    label,
    helpElement,
    labelExtra,
    errorElement,
    inputState = 'normal',
    disabled,
    pickerProps,
    name,
    required,
    labelSize,
    labelWeight,
    labelCapitalize = true,
}: DatePickerProps) => {
    const elementId = useId();
    const computedId = id || elementId;
    const { t } = useTranslation(['common']);
    const { formatDate } = useDateFormats();

    let suffix = <Calendar className="size-4 text-neutral-800" />;

    if (inputState === 'invalid') {
        suffix = <ExclamationCircleIcon aria-hidden="true" className="size-5 text-red-500" />;
    } else if (inputState === 'valid') {
        suffix = <CheckCircleIcon aria-hidden="true" className="size-5 text-green-500" />;
    }

    const input = (
        <Popover className="relative w-full">
            <PopoverButton
                as={InputGroup}
                disabled={disabled}
                inputState={inputState}
                data-empty={!value || undefined}
                className="cursor-pointer text-neutral-900 data-disabled:cursor-not-allowed data-empty:text-neutral-400"
            >
                <span
                    className={clsx(
                        'w-full grow py-[7px] pr-1 text-base font-medium sm:py-[9px]',
                        value ? 'text-neutral-800' : 'text-neutral-400'
                    )}
                >
                    {value ? formatDate(value) : t('common:empty.pickDate')}
                </span>
                <span className="shrink-0 text-base text-neutral-800 select-none sm:text-sm/6">{suffix}</span>
            </PopoverButton>
            <PopoverPanel
                transition
                anchor="bottom"
                className={clsx(
                    // Anchor positioning
                    '[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)] data-[anchor~=end]:[--anchor-offset:6px] data-[anchor~=start]:[--anchor-offset:-6px] sm:data-[anchor~=end]:[--anchor-offset:4px] sm:data-[anchor~=start]:[--anchor-offset:-4px]',
                    // Base styles
                    'isolate w-max rounded-xl p-2',
                    // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                    'outline outline-transparent focus:outline-hidden',
                    // Handle scrolling when menu won't fit in viewport
                    'overflow-y-auto',
                    // Popover background
                    'bg-popover/75 backdrop-blur-xl',
                    // Shadows
                    'shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset',
                    // Transitions
                    'transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0',
                    // Add z-index to ensure it appears above modals
                    'z-[9999]'
                )}
            >
                <DayPicker
                    {...pickerProps}
                    mode="single"
                    selected={value}
                    onSelect={onChange}
                    classNames={classNames}
                    captionLayout="dropdown"
                    startMonth={new Date(new Date().getFullYear() - 100, 0, 1)} // Set the start month to January 1st, 100 years ago, to allow selecting dates within the last century.
                    endMonth={new Date(new Date().getFullYear() + 100, 11, 31)} // Set the end month to December 31st, 100 years from now, to allow selecting dates within the next century.
                />
            </PopoverPanel>
        </Popover>
    );

    return (
        <Field
            name={name}
            id={computedId}
            label={label}
            errorElement={errorElement}
            helpElement={helpElement}
            labelExtra={labelExtra}
            required={required}
            labelSize={labelSize}
            labelWeight={labelWeight}
            labelCapitalize={labelCapitalize}
        >
            {input}
        </Field>
    );
};

export default DatePicker;
